import { Download, Search, RotateCcw, QrCode, Clock } from 'lucide-react'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
  InputGroup
} from 'react-bootstrap'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import Pdf from '../../../components/Download/Pdf'
import ModalDownloadQr from '../../../components/modal/pallete/ModalDownloadQr'
import api from '../../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import { firstLetterUpperCase } from '../../../utils/TextFormatter'
import { useLocation, useNavigate } from 'react-router-dom'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const typeWarehouses = ['All', 'ground', 'rack', 'shelving', 'double']

const formatTypeLabel = (t) => {
  if (!t) return ''
  if (t === 'double') return 'Double Deep'
  if (t === 'rack') return 'Selective'
  return firstLetterUpperCase(t.replaceAll('_', ' '))
}

const MasterRackDetailPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()

  const [showDownloadQr, setShowDownloadQr] = useState(false)
  const [showDownloadPdf, setShowDownloadPdf] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingDownload, setLoadingDownload] = useState(false)

  const [slots, setSlots] = useState([])
  const [checkedSlots, setCheckedSlots] = useState([])
  const [editId, setEditId] = useState({
    src: '',
    paletName: '',
    id: ''
  })

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const [warehouses, setWarehouses] = useState([])

  const [selectedWarehouse, setSelectedWarehouse] = useState(
    state?.warehouseId || null
  )
  const [selectedType, setSelectedType] = useState(state?.typeWarehouse || '')

  const [searchQ, setSearchQ] = useState(state?.q || '')
  const [debouncedQ, setDebouncedQ] = useState(searchQ)

  const [searchSlotName, setSearchSlotName] = useState(state?.slot || '')
  const [debouncedSlotName, setDebouncedSlotName] = useState(searchSlotName)

  const [searchPallet, setSearchPallet] = useState(state?.pallet || '')
  const [debouncedPallet, setDebouncedPallet] = useState(searchPallet)

  const [searchBatch, setSearchBatch] = useState(state?.batch || '')
  const [debouncedBatch, setDebouncedBatch] = useState(searchBatch)
  const [slotStatus, setSlotStatus] = useState('all')

  const prevFetchParams = useRef(null)

  const prevFilters = useRef({
    warehouseId: selectedWarehouse,
    typeWarehouse: selectedType,
    q: debouncedQ,
    slot: debouncedSlotName,
    pallet: debouncedPallet,
    batch: debouncedBatch,
    slot_status: slotStatus
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      requestAnimationFrame(() => setDebouncedQ(searchQ))
    }, 500)
    return () => clearTimeout(handler)
  }, [searchQ])

  useEffect(() => {
    const handler = setTimeout(() => {
      requestAnimationFrame(() => setDebouncedSlotName(searchSlotName))
    }, 500)
    return () => clearTimeout(handler)
  }, [searchSlotName])

  useEffect(() => {
    const handler = setTimeout(() => {
      requestAnimationFrame(() => setDebouncedPallet(searchPallet))
    }, 500)
    return () => clearTimeout(handler)
  }, [searchPallet])

  useEffect(() => {
    const handler = setTimeout(() => {
      requestAnimationFrame(() => setDebouncedBatch(searchBatch))
    }, 500)
    return () => clearTimeout(handler)
  }, [searchBatch])

  const fetchWarehouse = async () => {
    try {
      const params = { is_parent: 1, forUser: 1 }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const options = data.map((w) => ({
        value: w.id,
        label: w.warehouse_name
      }))
      setWarehouses(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const fetchSlots = useCallback(async () => {
    if (!selectedWarehouse) {
      setSlots([])
      prevFetchParams.current = null
      prevFilters.current = {
        warehouseId: null,
        typeWarehouse: '',
        q: '',
        slot: '',
        pallet: '',
        batch: '',
        slot_status: 'all'
      }
      return
    }

    const currentFilters = {
      warehouseId: selectedWarehouse,
      typeWarehouse: selectedType,
      q: debouncedQ,
      slot: debouncedSlotName,
      pallet: debouncedPallet,
      batch: debouncedBatch,
      slot_status: slotStatus
    }

    const isFilterChanged =
      JSON.stringify(prevFilters.current) !== JSON.stringify(currentFilters)

    const pageToFetch = isFilterChanged ? 1 : meta.current_page

    const params = {
      per_page: meta.per_page,
      page: pageToFetch,
      warehouse_id: selectedWarehouse,
      rack_type: selectedType === 'All' ? '' : selectedType,
      q: debouncedQ,
      slot: debouncedSlotName,
      pallet: debouncedPallet,
      batch: debouncedBatch,
      slot_status: slotStatus === 'all' ? '' : slotStatus
    }

    const paramsString = JSON.stringify(params)
    if (paramsString === prevFetchParams.current) return

    setLoading(true)
    try {
      setCheckedSlots([])

      const res = await api.get('master/slots', { params })
      const { metadata, data } = res.data
      setSlots(data)
      setMeta(metadata)

      prevFetchParams.current = paramsString

      if (isFilterChanged) {
        prevFilters.current = currentFilters
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }, [
    meta.per_page,
    meta.current_page,
    selectedWarehouse,
    selectedType,
    debouncedQ,
    debouncedSlotName,
    debouncedPallet,
    debouncedBatch,
    slotStatus
  ])

  useEffect(() => {
    fetchWarehouse()
  }, [])

  useEffect(() => {
    fetchSlots()
  }, [fetchSlots])

  const handleReset = () => {
    setSelectedWarehouse(null)
    setSelectedType('')
    setSearchQ('')
    setSearchSlotName('')
    setSearchPallet('')
    setSearchBatch('')
    setSlotStatus('all')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  function handleDownloadQr(data) {
    setShowDownloadQr(true)
    setEditId({
      ...editId,
      src: data.src,
      paletName: data.paletName,
      id: data.id
    })
  }

  const handleCheckInput = (e) => {
    const { value, checked } = e.target
    if (value === 'all') {
      setCheckedSlots([])
      if (checked) {
        const allData = slots.reduce((arr, cur) => {
          arr.push({
            id: cur.id,
            qr: cur.qr,
            slot_name: cur.current_location
          })
          return arr
        }, [])
        setCheckedSlots(allData)
      }
    } else {
      if (checked) {
        setCheckedSlots([...checkedSlots, JSON.parse(value)])
      } else {
        setCheckedSlots(
          checkedSlots.filter(
            (checkedSlot) => checkedSlot.id !== JSON.parse(value)?.id
          )
        )
      }
    }
  }

  const shardingItems = (propsDataValue) => {
    const data = JSON.parse(propsDataValue).reduce((acc, cur) => {
      acc.push({ id: cur.id, qr: cur.qr, name: cur.slot_name })
      return acc
    }, [])
    const shardedData = []

    for (let i = 0; i < data.length; i += 2) {
      shardedData.push(data.slice(i, i + 2))
    }
    return shardedData
  }

  const handleRedirectDetail = (value) => {
    navigate(`/master/rack-details/detail/${value.id}`, {
      state: {
        code: value.current_location,
        warehouseId: selectedWarehouse,
        typeWarehouse: selectedType,
        q: debouncedQ,
        slot: debouncedSlotName,
        pallet: debouncedPallet,
        batch: debouncedBatch,
        slot_status: slotStatus
      }
    })
  }

  const handleDownloadExport = async () => {
    try {
      setLoadingDownload(true)
      const res = await api.get('/master/stocks/get-data-stock', {
        params: {
          warehouse_id: selectedWarehouse
        },
        responseType: 'arraybuffer'
      })
      const { status, data: dataResponse } = res
      if (status === 200) {
        const blob = new Blob([dataResponse], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const warehouseNameFile = warehouses.find(
          (warehouse) => warehouse.value === selectedWarehouse
        )
        const fileName = `export_stock_${warehouseNameFile?.label
          ?.replaceAll(' ', '_')
          ?.toLowerCase()}.xlsx`
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const err = error?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(err)
    } finally {
      setLoadingDownload(false)
    }
  }

  const TbodyRow = ({ data }) => {
    const dataValue = {
      src: data.qr,
      paletName: data.current_location,
      id: data.id
    }
    const colorBackground = (slot_status) => {
      if (slot_status === 'invalid') {
        return { backgroundColor: '#ffebe6', color: '#d63939' }
      }
      if (slot_status === 'no_suggest') {
        return { backgroundColor: '#fff8e6', color: '#f59f00' }
      }
      return { backgroundColor: 'white', color: '#333' }
    }

    const style = colorBackground(data.slot_status)
    const cellStyle = { ...style, padding: '1rem' }

    return (
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td className="align-middle" style={cellStyle}>
          <Form.Check
            type="checkbox"
            value={JSON.stringify({
              id: data.id,
              slot_name: data.slot_name,
              qr: data.qr
            })}
            checked={
              !!checkedSlots.find((checkedSlot) => checkedSlot.id === data.id)
            }
            onChange={handleCheckInput}
          />
        </td>
        <td className="align-middle fw-semibold" style={cellStyle}>
          {data.current_location}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data?.palet_name || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {firstLetterUpperCase(data.type) || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.area || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.item_code || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.item_name || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.batch || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.createdAt ? convertDateWithoutTime(data.createdAt) : '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.exp_date ? convertDateWithoutTime(data.exp_date) : '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.total_pack || '-'}
        </td>
        <td className="align-middle" style={cellStyle}>
          {data.total_qty || '-'}
        </td>
        <td className="align-middle text-end" style={cellStyle}>
          <div className="d-flex flex-row justify-content-end gap-2">
            <Button
              variant="light"
              size="sm"
              className="rounded border d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
              onClick={() => handleDownloadQr(dataValue)}
              title="Print QR"
            >
              <QrCode size={18} className="text-secondary" />
            </Button>
            <Button
              variant="light"
              size="sm"
              className="rounded border d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
              onClick={() => handleRedirectDetail(data)}
              title="View Details"
            >
              <Clock size={18} className="text-secondary" />
            </Button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Rack Detail</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCcw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                options={warehouses}
                value={warehouses.find((w) => w.value === selectedWarehouse)}
                onChange={(e) => setSelectedWarehouse(e?.value)}
                styles={SelectStyles}
                placeholder="Select Warehouse"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Type</Form.Label>
              <Select
                options={typeWarehouses.map((t) => ({
                  value: t,
                  label: formatTypeLabel(t)
                }))}
                value={
                  selectedType
                    ? {
                        value: selectedType,
                        label: formatTypeLabel(selectedType)
                      }
                    : null
                }
                onChange={(e) => setSelectedType(e?.value)}
                styles={SelectStyles}
                placeholder="Select Type"
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Slot Name</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Slot..."
                  value={searchSlotName}
                  onChange={(e) => setSearchSlotName(e.target.value)}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Pallet ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search Pallet ID"
                value={searchPallet}
                onChange={(e) => setSearchPallet(e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Batch</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search Batch"
                value={searchBatch}
                onChange={(e) => setSearchBatch(e.target.value)}
                style={FormControlStyle}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Item Name / Code</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Item..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mb-3 justify-content-between align-items-center">
        <Col md="auto">
          <div className="d-flex align-items-center gap-3">
            <Form.Check
              type="radio"
              id="view-all"
              label="View all slot"
              name="slotStatus"
              value="all"
              checked={slotStatus === 'all'}
              onChange={(e) => setSlotStatus(e.target.value)}
              className="fw-bold text-secondary"
            />
            <Form.Check
              type="radio"
              id="view-empty"
              label="View empty slot"
              name="slotStatus"
              value="empty"
              checked={slotStatus === 'empty'}
              onChange={(e) => setSlotStatus(e.target.value)}
              className="fw-bold text-secondary"
            />
            <Form.Check
              type="radio"
              id="view-filled"
              label="View filled slot"
              name="slotStatus"
              value="used"
              checked={slotStatus === 'used'}
              onChange={(e) => setSlotStatus(e.target.value)}
              className="fw-bold text-secondary"
            />
          </div>
        </Col>
        <Col md="auto">
          <div className="d-flex gap-2">
            <Button
              variant={
                !selectedWarehouse || slots.length === 0 || loadingDownload
                  ? 'secondary'
                  : 'warning'
              }
              className={`rounded px-4 fw-bold ${
                !selectedWarehouse || slots.length === 0 || loadingDownload
                  ? 'opacity-50'
                  : 'text-white'
              }`}
              onClick={handleDownloadExport}
              disabled={
                !selectedWarehouse || slots.length === 0 || loadingDownload
              }
            >
              <Download size={18} className="me-2" />
              {loadingDownload ? 'Exporting...' : 'Export'}
            </Button>

            {checkedSlots.length > 0 ? (
              <PDFDownloadLink
                document={
                  <Pdf data={shardingItems(JSON.stringify(checkedSlots))} />
                }
                fileName="print_qr_slot.pdf"
                onClick={() => {
                  setShowDownloadPdf(true)
                  setTimeout(() => {
                    setShowDownloadPdf(false)
                  }, 1000)
                }}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="secondary"
                  className="rounded px-4 fw-bold"
                  disabled={showDownloadPdf}
                >
                  <QrCode size={18} className="me-2" />
                  {showDownloadPdf ? 'Preparing...' : 'Print QR'}
                </Button>
              </PDFDownloadLink>
            ) : (
              <Button
                variant="secondary"
                className="rounded px-4 opacity-50 fw-bold"
                disabled
              >
                <QrCode size={18} className="me-2" />
                Print QR
              </Button>
            )}
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="p-2 align-middle bg-light border-bottom">
                  <Form.Check
                    type="checkbox"
                    checked={
                      checkedSlots.length === slots.length && slots.length > 0
                    }
                    onChange={handleCheckInput}
                    value="all"
                    disabled={slots.length === 0}
                  />
                </th>
                {[
                  'Slot Name',
                  'Palet ID',
                  'Type',
                  'Area',
                  'Item Code',
                  'Item Name',
                  'Batch',
                  'Received',
                  'Exp Date',
                  'Qty Kemasan',
                  'Base Qty UOM'
                ].map((h) => (
                  <th
                    key={h}
                    className="p-2 align-middle bg-light border-bottom text-uppercase text-secondary"
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {h}
                  </th>
                ))}
                <th
                  className="p-2 align-middle bg-light border-bottom text-end text-uppercase text-secondary"
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    letterSpacing: '0.5px'
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={12} className="text-center p-5">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loading && slots.length === 0 && <RowEmpty colSpan={12} />}
              {!loading &&
                slots.length > 0 &&
                slots.map((slot, index) => (
                  <TbodyRow key={index} data={slot} />
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={13} className="p-3 border-top-0">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col md={12}>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#f2a324',
                  borderRadius: '50%'
                }}
              ></div>
              <small className="text-muted fw-bold">No Suggest Slot</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#f35724',
                  borderRadius: '50%'
                }}
              ></div>
              <small className="text-muted fw-bold">Invalid Slot</small>
            </div>
          </div>
        </Col>
      </Row>

      <ModalDownloadQr
        show={showDownloadQr}
        onHide={() => {
          setShowDownloadQr(false)
          setEditId({ ...editId, src: '', paletName: '', id: '' })
        }}
        type="Slot"
        paletData={editId}
      />
    </Container>
  )
}

export default MasterRackDetailPage
